import { useState, useEffect } from 'react'
import type { SavedDocument } from '@creanizer/types'
import { Button, Input, Badge, ScrollArea } from '@creanizer/ui'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '@creanizer/ui'
import { FileText, Calendar, Tag, Trash2, ExternalLink, Globe, Users, PenTool, ChevronRight, Search, Eye, Edit, Download, Folder, Star } from 'lucide-react'
import { useDocumentStore } from '../../../../stores/documentStore'
import { useFolderStore } from '../../../../stores/folderStore'
import { ExportImportButtons } from '../../../../components/common'
import { DocumentEditModal } from './DocumentEditModal'
// import { FolderManager, FolderMoveSelect } from '../ItemGenerator/FolderManager'

interface SavedDocumentsListProps {
  onLoad: (document: SavedDocument) => void
  onContinue?: (document: SavedDocument, targetType?: string) => void
}

export function SavedDocumentsList({ onLoad, onContinue }: SavedDocumentsListProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [editingDocument, setEditingDocument] = useState<SavedDocument | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [showFolders, setShowFolders] = useState(true)
  const [draggedDocumentId, setDraggedDocumentId] = useState<string | null>(null)
  
  const { 
    documents, 
    removeDocument, 
    clearDocuments, 
    exportAsText, 
    exportAsJSON, 
    exportSingleAsText, 
    exportSingleAsJSON, 
    importFromJSON,
    updateDocument,
    toggleFavorite
  } = useDocumentStore()
  
  const { getItemsInFolder, getItemFolder, moveItemBetweenFolders } = useFolderStore()
  
  // 文献タイプのラベル取得
  const getDocumentTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      'newspaper': '新聞記事',
      'sns_twitter': 'Twitter/X',
      'diary': '日記',
      'report': '報告書',
      'blog': 'ブログ',
      'email': 'メール',
      'letter': '手紙',
      'memo': 'メモ',
      'medical': '医療記録',
      'police': '警察調書',
      'academic': '学術論文',
      'government': '政府文書',
      'magazine': '雑誌記事',
      'line': 'LINE会話',
      'history': '歴史書',
      'custom': 'カスタム文献'
    }
    return labels[type] || type
  }
  
  // 元の文献タイプから推奨される続きのタイプを取得
  const getRecommendedTypes = (sourceType: string) => {
    const recommendations: Record<string, Array<{value: string; label: string; description: string}>> = {
      'newspaper': [
        { value: 'sns_twitter', label: 'Twitter/X', description: '記事への反応・議論' },
        { value: 'blog', label: 'ブログ', description: '詳細な解説・意見' },
        { value: 'diary', label: '日記', description: '個人的な感想' }
      ],
      'sns_twitter': [
        { value: 'newspaper', label: '新聞記事', description: '話題を報道' },
        { value: 'blog', label: 'ブログ', description: '詳しい考察' },
        { value: 'diary', label: '日記', description: '出来事の記録' }
      ],
      'blog': [
        { value: 'sns_twitter', label: 'Twitter/X', description: '記事の宣伝・反応' },
        { value: 'email', label: 'メール', description: '感想を送る' },
        { value: 'memo', label: 'メモ', description: '要点をまとめる' }
      ],
      'report': [
        { value: 'newspaper', label: '新聞記事', description: '報告書を報道' },
        { value: 'email', label: 'メール', description: '関係者への連絡' },
        { value: 'memo', label: 'メモ', description: '要点整理' }
      ],
      'letter': [
        { value: 'letter', label: '手紙', description: '返信を書く' },
        { value: 'diary', label: '日記', description: '手紙の感想' },
        { value: 'email', label: 'メール', description: '内容を転送' }
      ],
      'email': [
        { value: 'email', label: 'メール', description: '返信' },
        { value: 'memo', label: 'メモ', description: 'ToDoリスト' },
        { value: 'report', label: '報告書', description: '正式な報告' }
      ],
      'diary': [
        { value: 'blog', label: 'ブログ', description: '公開用に編集' },
        { value: 'letter', label: '手紙', description: '誰かに伝える' },
        { value: 'sns_twitter', label: 'Twitter/X', description: '一部を共有' }
      ],
      'memo': [
        { value: 'report', label: '報告書', description: '正式文書化' },
        { value: 'email', label: 'メール', description: '共有する' },
        { value: 'blog', label: 'ブログ', description: '記事にする' }
      ],
      'magazine': [
        { value: 'sns_twitter', label: 'Twitter/X', description: '記事への反応' },
        { value: 'blog', label: 'ブログ', description: '詳細なレビュー' },
        { value: 'letter', label: '手紙', description: '編集部への投書' }
      ],
      'line': [
        { value: 'diary', label: '日記', description: '会話の記録' },
        { value: 'blog', label: 'ブログ', description: '出来事をまとめる' },
        { value: 'memo', label: 'メモ', description: 'ToDoリスト化' }
      ],
      'history': [
        { value: 'academic', label: '学術論文', description: '学術的考察' },
        { value: 'newspaper', label: '新聞記事', description: '現代的な報道' },
        { value: 'blog', label: 'ブログ', description: '歴史解説記事' }
      ],
      'custom': [
        { value: 'blog', label: 'ブログ', description: '公開用に編集' },
        { value: 'report', label: '報告書', description: '正式文書化' },
        { value: 'memo', label: 'メモ', description: '要点整理' }
      ]
    }
    
    // デフォルトの推奨
    const defaultRecommendations = [
      { value: 'sns_twitter', label: 'Twitter/X', description: '反応・感想' },
      { value: 'blog', label: 'ブログ', description: '詳細な考察' },
      { value: 'memo', label: 'メモ', description: '要点整理' }
    ]
    
    return recommendations[sourceType] || defaultRecommendations
  }
  
  // フォルダによるフィルタリング
  let folderFilteredDocuments = documents
  if (selectedFolderId === 'all') {
    folderFilteredDocuments = documents
  } else if (selectedFolderId === null) {
    // 未整理: どのフォルダにも属していない文献
    const allFolderDocumentIds = new Set<string>()
    const folders = useFolderStore.getState().getFolders('document')
    folders.forEach(folder => {
      getItemsInFolder('document', folder.id).forEach(id => allFolderDocumentIds.add(id))
    })
    folderFilteredDocuments = documents.filter(doc => !allFolderDocumentIds.has(doc.id))
  } else {
    const documentIds = getItemsInFolder('document', selectedFolderId)
    folderFilteredDocuments = documents.filter(doc => documentIds.includes(doc.id))
  }
  
  // 検索によるフィルタリング
  const filteredDocuments = folderFilteredDocuments.filter(doc =>
    doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getDocumentTypeLabel(doc.result.type).toLowerCase().includes(searchTerm.toLowerCase()) ||
    (doc.tags && doc.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))) ||
    (doc.memo && doc.memo.toLowerCase().includes(searchTerm.toLowerCase()))
  )
  
  // 日付フォーマット
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  
  if (documents.length === 0 && !showFolders) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>保存済みの文献はありません</p>
      </div>
    )
  }
  
  return (
    <div className="card max-w-full overflow-x-hidden">
      <div className="flex items-center gap-2 mb-2">
        <FileText className="h-5 w-5" />
        <h2 className="text-xl font-semibold">
          保存済み文献 ({documents.length}/500)
        </h2>
      </div>
      <p className="text-muted-foreground mb-6">
        生成した文献を保存・管理
      </p>
      
      <div className="space-y-4 max-w-full">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="文献を検索..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFolders(!showFolders)}
            className="px-2"
          >
            <Folder className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex gap-2">
          <ExportImportButtons
            onExportText={exportAsText}
            onExportJSON={exportAsJSON}
            onImportJSON={async (jsonString: string) => {
              try {
                const data = JSON.parse(jsonString)
                const file = new File([jsonString], 'import.json', { type: 'application/json' })
                await importFromJSON(file)
              } catch (error) {
                console.error('インポートエラー:', error)
                alert('インポートに失敗しました')
              }
            }}
            dataTypeName="文献"
          />

          {documents.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (confirm('すべての文献を削除しますか？')) {
                  clearDocuments()
                }
              }}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              全削除
            </Button>
          )}
        </div>

        <div className="flex gap-4">
          {/* フォルダサイドバー */}
          {/* {showFolders && (
            <div className="w-48 border-r pr-4">
              <FolderManager
                generatorType="document"
                items={documents}
                selectedFolderId={selectedFolderId}
                onFolderSelect={setSelectedFolderId}
                draggedItemId={draggedDocumentId}
                onItemMove={(documentId, toFolderId) => {
                  const currentFolder = getItemFolder('document', documentId)
                  moveItemBetweenFolders('document', documentId, currentFolder?.id || null, toFolderId)
                }}
              />
            </div>
          )} */}
          
          {/* 文献リスト */}
          <div className="flex-1">
            <ScrollArea className="h-[500px] w-full overflow-x-hidden">
              <div className="space-y-2 max-w-full w-full">
                {filteredDocuments.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    {searchTerm ? '検索結果がありません' : '保存済みの文献がありません'}
                  </p>
                ) : (
                  filteredDocuments.map((doc) => (
                    <div 
                      key={doc.id} 
                      className="card p-2 sm:p-4 overflow-hidden max-w-full w-[calc(100%-2rem)] sm:w-full mr-auto cursor-move hover:shadow-lg transition-shadow"
                      draggable
                      onDragStart={(e) => {
                        setDraggedDocumentId(doc.id)
                        e.dataTransfer.effectAllowed = 'move'
                      }}
                      onDragEnd={() => {
                        setDraggedDocumentId(null)
                      }}
                      style={{
                        opacity: draggedDocumentId === doc.id ? 0.5 : 1
                      }}
                    >
                      {/* モバイル版: 縦並びレイアウト */}
                      <div className="flex flex-col gap-3 sm:hidden w-full">
                        <div className="w-full">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-sm truncate">{doc.title}</h3>
                            {doc.favorite && <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />}
                          </div>
                          
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                            <Badge variant="outline" className="text-xs">
                              {getDocumentTypeLabel(doc.result.type)}
                            </Badge>
                            <span className="text-xs">{formatDate(doc.createdAt)}</span>
                          </div>
                          
                          {doc.worldId && (
                            <span className="flex items-center gap-1 text-blue-600 text-xs mt-1">
                              <Globe className="w-3 h-3" />
                              世界観連携
                            </span>
                          )}
                          
                          {doc.characterIds && doc.characterIds.length > 0 && (
                            <span className="flex items-center gap-1 text-green-600 text-xs mt-1">
                              <Users className="w-3 h-3" />
                              キャラクター: {doc.characterIds.length}
                            </span>
                          )}
                          
                          {doc.tags && doc.tags.length > 0 && (
                            <div className="flex gap-1 mt-2">
                              {doc.tags.slice(0, 2).map((tag, i) => (
                                <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          {doc.memo && (
                            <p className="text-xs text-muted-foreground mt-2 italic truncate">
                              {doc.memo}
                            </p>
                          )}
                          
                          {/* フォルダ選択 */}
                          {/* <div className="mt-2">
                            <FolderMoveSelect
                              generatorType="document"
                              itemId={doc.id}
                              currentFolderId={getItemFolder('document', doc.id)?.id}
                              onMove={() => {}}
                            />
                          </div> */}
                        </div>
                        
                        {/* モバイル版: 横一列のボタン */}
                        <div className="flex gap-0.5 justify-start w-full flex-wrap">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onLoad(doc)}
                            title="読込"
                            className="w-12 h-7 px-1"
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingDocument(doc)}
                            title="編集"
                            className="w-12 h-7 px-1"
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          {onContinue && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 px-1"
                                >
                                  <PenTool className="h-3 w-3" />
                                  <span className="ml-1 text-xs">続き</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuItem onClick={() => {
                                  console.log('[SavedDocumentsList] Continuing with doc:', doc)
                                  console.log('[SavedDocumentsList] doc.characterIds:', doc.characterIds)
                                  console.log('[SavedDocumentsList] doc.input:', doc.input)
                                  console.log('[SavedDocumentsList] doc.input.characterIds:', doc.input?.characterIds)
                                  console.log('[SavedDocumentsList] doc.input.continuationFrom:', doc.input?.continuationFrom)
                                  console.log('[SavedDocumentsList] doc.input.continuationFrom.previousContents:', doc.input?.continuationFrom?.previousContents)
                                  onContinue(doc)
                                }}>
                                  <div>
                                    <div className="font-medium">同じタイプで続き</div>
                                    <div className="text-xs text-gray-600">
                                      {getDocumentTypeLabel(doc.result.type)}として続きを作成
                                    </div>
                                  </div>
                                </DropdownMenuItem>
                                
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel className="text-xs">
                                  別タイプで反応・関連文献
                                </DropdownMenuLabel>
                                {getRecommendedTypes(doc.result.type).map(type => (
                                  <DropdownMenuItem
                                    key={type.value}
                                    onClick={() => {
                                      console.log('[SavedDocumentsList] Continuing with different type:', type.value)
                                      console.log('[SavedDocumentsList] From doc type:', doc.result.type)
                                      console.log('[SavedDocumentsList] doc.input.continuationFrom:', doc.input?.continuationFrom)
                                      onContinue(doc, type.value)
                                    }}
                                  >
                                    <div>
                                      <div className="font-medium">{type.label}</div>
                                      <div className="text-xs text-gray-600">{type.description}</div>
                                    </div>
                                  </DropdownMenuItem>
                                ))}
                                
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => onContinue(doc, undefined)}
                                  className="text-blue-600"
                                >
                                  その他のタイプから選択...
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleFavorite(doc.id)}
                            title="お気に入り"
                            className="w-12 h-7 px-1"
                          >
                            <Star className={`h-3 w-3 ${doc.favorite ? 'text-yellow-500 fill-yellow-500' : ''}`} />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => exportSingleAsText(doc.id)}
                            title="テキスト形式でエクスポート"
                            className="w-14 h-7 px-0.5 text-[9px]"
                          >
                            <Download className="h-2.5 w-2.5" />
                            <span className="ml-0.5">txt</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => exportSingleAsJSON(doc.id)}
                            title="JSON形式でエクスポート"
                            className="w-14 h-7 px-0.5 text-[9px]"
                          >
                            <Download className="h-2.5 w-2.5" />
                            <span className="ml-0.5">json</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (confirm('この文献を削除しますか？')) {
                                removeDocument(doc.id)
                              }
                            }}
                            className="w-12 h-7 px-1"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* PC版: 既存の横並びレイアウト */}
                      <div className="hidden sm:flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-sm truncate">{doc.title}</h3>
                            {doc.favorite && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                          </div>
                          
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1 flex-wrap">
                            <Badge variant="outline">{getDocumentTypeLabel(doc.result.type)}</Badge>
                            <span>{formatDate(doc.createdAt)}</span>
                            {doc.worldId && (
                              <span className="flex items-center gap-1 text-blue-600">
                                <Globe className="w-3 h-3" />
                                世界観連携
                              </span>
                            )}
                            {doc.characterIds && doc.characterIds.length > 0 && (
                              <span className="flex items-center gap-1 text-green-600">
                                <Users className="w-3 h-3" />
                                キャラクター: {doc.characterIds.length}
                              </span>
                            )}
                          </div>
                          
                          {doc.tags && doc.tags.length > 0 && (
                            <div className="flex gap-1 mt-2">
                              {doc.tags.map((tag, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-primary/10 rounded text-xs"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          {doc.memo && (
                            <p className="text-sm text-muted-foreground mt-2 italic">
                              {doc.memo}
                            </p>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          <div className="flex flex-col gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onLoad(doc)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              読込
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => exportSingleAsText(doc.id)}
                              title="テキスト形式でエクスポート"
                            >
                              <Download className="h-4 w-4 mr-1" />
                              txt
                            </Button>
                          </div>
                          
                          <div className="flex flex-col gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingDocument(doc)}
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              編集
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => exportSingleAsJSON(doc.id)}
                              title="JSON形式でエクスポート"
                            >
                              <Download className="h-4 w-4 mr-1" />
                              json
                            </Button>
                          </div>
                          
                          {onContinue && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex items-center gap-1"
                                >
                                  <PenTool className="h-4 w-4" />
                                  続き
                                  <ChevronRight className="h-3 w-3" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuItem onClick={() => {
                                  console.log('[SavedDocumentsList] Continuing with doc:', doc)
                                  console.log('[SavedDocumentsList] doc.characterIds:', doc.characterIds)
                                  console.log('[SavedDocumentsList] doc.input:', doc.input)
                                  console.log('[SavedDocumentsList] doc.input.characterIds:', doc.input?.characterIds)
                                  console.log('[SavedDocumentsList] doc.input.continuationFrom:', doc.input?.continuationFrom)
                                  console.log('[SavedDocumentsList] doc.input.continuationFrom.previousContents:', doc.input?.continuationFrom?.previousContents)
                                  onContinue(doc)
                                }}>
                                  <div>
                                    <div className="font-medium">同じタイプで続き</div>
                                    <div className="text-xs text-gray-600">
                                      {getDocumentTypeLabel(doc.result.type)}として続きを作成
                                    </div>
                                  </div>
                                </DropdownMenuItem>
                                
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel className="text-xs">
                                  別タイプで反応・関連文献
                                </DropdownMenuLabel>
                                {getRecommendedTypes(doc.result.type).map(type => (
                                  <DropdownMenuItem
                                    key={type.value}
                                    onClick={() => {
                                      console.log('[SavedDocumentsList] Continuing with different type:', type.value)
                                      console.log('[SavedDocumentsList] From doc type:', doc.result.type)
                                      console.log('[SavedDocumentsList] doc.input.continuationFrom:', doc.input?.continuationFrom)
                                      onContinue(doc, type.value)
                                    }}
                                  >
                                    <div>
                                      <div className="font-medium">{type.label}</div>
                                      <div className="text-xs text-gray-600">{type.description}</div>
                                    </div>
                                  </DropdownMenuItem>
                                ))}
                                
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => onContinue(doc, undefined)}
                                  className="text-blue-600"
                                >
                                  その他のタイプから選択...
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleFavorite(doc.id)}
                          >
                            <Star className={`h-4 w-4 ${doc.favorite ? 'text-yellow-500 fill-yellow-500' : ''}`} />
                          </Button>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (confirm('この文献を削除しますか？')) {
                                removeDocument(doc.id)
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        {documents.length >= 450 && (
          <p className="text-xs text-muted-foreground text-center">
            保存可能件数の上限（500件）に近づいています
          </p>
        )}
      </div>
      
      {editingDocument && (
        <DocumentEditModal
          isOpen={!!editingDocument}
          onClose={() => setEditingDocument(null)}
          onSave={(updatedDocument) => {
            updateDocument(editingDocument.id, {
              ...updatedDocument,
              updatedAt: new Date().toISOString()
            })
            setEditingDocument(null)
          }}
          document={editingDocument}
        />
      )}
    </div>
  )
}